import { AssemblyModalities } from 'helpers/enums/Assembleia';
import {
  AssembleiaPautaStatus,
  TipoVotoPauta,
} from 'helpers/enums/AssembleiaPautaStatus';
import { AssembleiaStatus } from 'helpers/enums/AssembleiaStatus';
import { AssembleiaTipo } from 'helpers/enums/AssembleiaTipo';
import { FlagColaborador } from 'helpers/enums/TipoColaborador';
import { contaSelecionadaPeloUsuario } from 'helpers/utils/AssembleiaUtils';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import { pautaUtilsGetVotoByIdPauta } from 'helpers/utils/PautaUtils';
import { AssociadoRoutePath } from 'library/Routes';
import translation from 'locales/translation';
import {
  ConfirmModal,
  ModalSizes,
  ScreenSize,
} from 'plataforma-fundacao-componentes';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { closeModal, openModal } from 'store/modules/ModalActions';
import { IReducer } from 'store/Reducer';

export default function useAllowChangeRoute(): [() => Promise<void>] {
  const reducer = useSelector((state: IReducer) => state);
  const dispatch = useDispatch();

  const usuario = useMemo(() => {
    return reducer.auth.usuario;
  }, [reducer.auth.usuario]);

  const isLogged = useMemo((): boolean => {
    return reducer.auth.logged;
  }, [reducer.auth.logged]);

  const evento = useMemo(
    () => reducer.assembleias.currentEvent,
    [reducer.assembleias.currentEvent]
  );

  const contas = useMemo(() => {
    return evento!?.contasDisponiveis || [];
  }, [evento]);

  const pautas = useMemo(() => evento?.pautas || [], [evento]);

  const matchAssembleiaAssociadoRoute = useRouteMatch({
    path: `${AssociadoRoutePath}/assembleia/:id`,
  });

  const inAssembleiaLive = useMemo(
    () =>
      matchAssembleiaAssociadoRoute &&
      evento!?.status === AssembleiaStatus.Live,
    [evento, matchAssembleiaAssociadoRoute]
  );

  const hasOpenedPautasWithoutVote = useMemo(() => {
    const pautasParaVotacao = pautas!.filter((pauta) => {
      if (
        pauta.status === AssembleiaPautaStatus.Bloqueada &&
        pauta.itens.length > 0
      )
        return true;
      if (
        pauta.status === AssembleiaPautaStatus.Liberada &&
        pauta.votos.length === 0 &&
        !pautaUtilsGetVotoByIdPauta(usuario!.cpfCnpj, pauta.idPauta) &&
        (pauta.tipoVotoPauta === TipoVotoPauta.Vota ||
          pauta.tipoVotoPauta === TipoVotoPauta.VotaNaoColaborador ||
          pauta.tipoVotoPauta === TipoVotoPauta.VotaNaoRestrito)
      ) {
        return true;
      }
      return false;
    });
    return pautasParaVotacao.length > 0;
  }, [pautas, usuario]);

  const isConvocado = useMemo(() => {
    return (
      contas.length && contas.some((conta) => conta.idConvocado !== undefined)
    );
  }, [contas]);

  const isColaborador = useMemo(() => {
    const contasSelecionadas = contas.filter((conta) =>
      contaSelecionadaPeloUsuario(conta, usuario?.cpfCnpj!)
    );
    return (
      contasSelecionadas.length &&
      contasSelecionadas.every(
        (conta) => conta.flgColaborador === FlagColaborador.Colaborador
      )
    );
  }, [usuario, contas]);

  const hasPresencaPresencial = useMemo(() => {
    return contas.some((conta) => conta.tipoPresenca === 'ONLINE');
  }, [contas]);

  const hasPermissionToParticipate = useMemo(() => {
    if (
      evento?.modoEvento === AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL &&
      hasPresencaPresencial
    ) {
      return false;
    }
    if (isColaborador) return false;
    if (
      evento?.tipo !== AssembleiaTipo.Nucleo &&
      evento?.tipo !== AssembleiaTipo.Reuniao &&
      !evento?.userCanVote
    ) {
      return false;
    }
    return Boolean(
      isConvocado &&
        usuario?.perfil.associadoRoute.assembleias.pautas.participar
    );
  }, [evento, hasPresencaPresencial, isColaborador, isConvocado, usuario]);

  const lockedAccess = useMemo(() => {
    if (
      isLogged &&
      inAssembleiaLive &&
      hasOpenedPautasWithoutVote &&
      hasPermissionToParticipate
    ) {
      let text = [
        AssembleiaTipo.GeralOrdinaria as any,
        AssembleiaTipo.GeralExtraordinaria as any,
        AssembleiaTipo.GeralOrdinariaExtraordinaria as any,
      ].includes(evento!?.tipo || '')
        ? translation.FugaAlert.assembleiaAssociadoLeaveMessage
            .leaveMessageAGOAGEAOE
        : translation.FugaAlert.assembleiaAssociadoLeaveMessage.leaveMessage;

      return {
        title: translation.FugaAlert.assembleiaAssociadoLeaveTitle,
        text,
      };
    }
    return null;
  }, [
    evento,
    hasOpenedPautasWithoutVote,
    hasPermissionToParticipate,
    inAssembleiaLive,
    isLogged,
  ]);

  const onClick = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        if (lockedAccess) {
          const mk = getUniqueKey();
          setTimeout(() => {
            dispatch(
              openModal(ConfirmModal, {
                modalKey: mk,
                size: ModalSizes.Small,
                title: lockedAccess.title,
                mobileOn: ScreenSize.xs,
                children: lockedAccess.text,
                onClose: () => {
                  dispatch(closeModal(mk));
                  reject();
                },
                onCancel: () => {
                  dispatch(closeModal(mk));
                  reject();
                },
                onConfirm: () => {
                  dispatch(closeModal(mk));
                  resolve();
                },
              })
            );
          }, 50);
        } else {
          resolve();
        }
      }),
    [dispatch, lockedAccess]
  );

  return [onClick];
}
